import { getOffsets } from 'util/indexOffsets';
import { chartEngine } from '../constants/chart-constants';
import { dataDefaults, seriesDefaults } from '../constants/marketdata';
import * as analytics from './analytics';
import { getName } from './econdata';
import { getLastValue, parseData, parseDataColumn } from './marketdata';
import { LONG } from '../../chart/data/constants';

export default function importData(type, parsed, options = {}) {
  const dataType = getDataType(type, options.econType);
  analytics.track('event', 'Data', 'importData', dataType);

  switch (type) {
    case 'econ':
      return importEconData(type, parsed, options);
    case 'market':
      return importMarketData(type, parsed, options);
    case 'series':
      return importSeriesData(type, parsed, options);
    case 'tabular':
      return importTabularData(type, parsed, options);
    default:
      return importTabularData('tabular', parsed, options);
  }
}

export function createCategories(data, categoryOffset) {
  return data.map(({ y }) => y).slice(categoryOffset);
}

function importEconData(type, parsed, options) {
  const { categories, allSeries, econType, seriesObj, frequency } = options;
  const series = parsed.map((observation) => {
    const { data, symbol } = observation;
    const name = getName(econType, allSeries, symbol, seriesObj);
    return { ...dataDefaults, data: parseData(data), name, symbol };
  });
  const dates = createDates(categories);
  const [dateSpan, interval] = findDateSpan(dates);
  return {
    ...seriesDefaults,
    type,
    parsed,
    series,
    categories,
    quarterlyResults: frequency === 'q',
    dateSpan,
    interval,
  };
}

function importMarketData(type, parsed, options) {
  const { chartType, names } = options;
  const action = getAction(chartType);
  return action(names, parsed, type);
}

function getAction(type) {
  switch (type) {
    case 'bar':
      return parseMarketBar;
    case 'column':
      return parseMarketColumn;
    default:
      return parseMarketLine;
  }
}

const getTickerName = (symbol, names, name, i) => {
  const isMarketWatch = window.location.pathname.split('/')[1] === 'marketwatch';
  if (isMarketWatch) return symbol;
  return names ? names[i] : name;
};

function parseMarketLine(names, parsed, type) {
  const { date, isIntraday, needsCommas, showAsPercentProps, categories } = parsed[0];
  const series = parsed.map((ticker, i) => {
    const { name, symbol, data, prefix, suffix, decimals, tickerType } = ticker;

    const tickerName = getTickerName(symbol, names, name, i);

    return {
      ...dataDefaults,
      data: parseData(data),
      name: tickerName,
      symbol,
      prefix,
      suffix,
      decimals,
      tickerType,
    };
  });
  const { isPercent } = showAsPercentProps;
  const dates = createDates(categories);
  const [dateSpan, interval] = findDateSpan(dates);
  return {
    ...seriesDefaults,
    type,
    parsed,
    series,
    marketTime: date,
    isIntraday,
    isPercent,
    needsCommas,
    categories,
    dateSpan,
    interval,
  };
}

function parseMarketColumn(names, parsed, type) {
  const { date, isIntraday, categories, name, symbol, data } = parsed[0];
  const series = [
    {
      ...dataDefaults,
      data: parseDataColumn(data),
      name: names ? names[0] : name,
      symbol,
      prefix: '',
      suffix: '%',
      decimals: 1,
    },
  ];
  const updatedCategories = categories.slice(1);
  const dates = createDates(updatedCategories);
  const [dateSpan, interval] = findDateSpan(dates);
  return {
    ...seriesDefaults,
    type,
    parsed,
    series,
    marketTime: date,
    isIntraday,
    isPercent: true,
    needsCommas: true,
    categories: updatedCategories,
    dateSpan,
    interval,
  };
}

/**
 * Calculates percent change(last data point) during selected time frame,
 * if the new categories order differs from existing, updates the order and applies existing label names.
 * @param {[{symbol: String, name: String}]} names - Existing data. Array of objects with symbol and name properties. Symbol is used as an unique identifier.
 * @param {[{symbol:String, name: String, data: Array}]} parsed - New data from the api.
 * @param {String} type - chart type
 * @return {{categoriesAreDates:Boolean, series:Array, categories:Array, type:string, parsed:Array}}
 */

function parseMarketBar(names, parsed, type) {
  const categories = parsed.map(({ symbol: currentSymbol, name: currentName }) => {
    const nameIdx = names?.findIndex(({ symbol }) => symbol === currentSymbol);
    return nameIdx > -1 ? names[nameIdx].name : currentName;
  });

  const series = [
    {
      data: parsed.map(({ data }) => getLastValue(data)),
      name: 'Percent change',
    },
  ];
  return {
    categoriesAreDates: false,
    series,
    categories,
    type,
    parsed,
  };
}

// Series data includes JSON.
function importSeriesData(type, parsed, options) {
  const series = [];
  const categories = [];

  for (let i = 0, len = parsed.length; i < len; i++) {
    series[i] = {
      name: parsed[i].name || `Series ${i + 1}`,
      showInLegend: true,
      data: parsed[i].data.map((item, index) => {
        const point = {};
        if (Array.isArray(item)) {
          if (typeof item[0] === 'string') {
            categories[index] = item[0];
            point.name = item[0];
            point.y = toNum(item[1]);
          } else if (item.length > 1) {
            point.x = toNum(item[0]);
            point.y = toNum(item[1]);
          } else {
            point.y = toNum(item[0]);
          }
          return point;
        }
        return item;
      }),
      line: 'solid',
    };
  }

  return {
    type,
    parsed,
    series,
    categories,
    firstRowAsNames: false,
    firstColumnAsCategories: false,
    categoriesAreDates: false,
  };
}

function importTabularData(type, parsed, options) {
  parsed = removeEmptyLines(parsed);
  let series = sortParsed(parsed, options);

  // Return null if any indexes correspond to a blank row or junk data.
  if (isJunk(series)) {
    return null;
  } /* else if (hasUndefinedItems(series)) {
    return null;
  } */

  const firstRowAsNames = getFirstRowAsNames(series, options);
  const { categoryOffset } = getOffsets({ firstRowAsNames });
  const categories = createCategories(series[0].data, categoryOffset);

  let firstColumnAsCategories = false;
  let categoriesAreDates = false;
  const categoriesAreMonthly = false;
  let fiscalYears = false;

  const dates = chartEngine.datetime.parse(categories, options);
  const quarterlyResults = isQuarterly(dates);
  const all = chartEngine.datetime.all(dates);
  fiscalYears = all.fiscalYear;
  if (chartEngine.datetime.allValidDates(dates)) {
    firstColumnAsCategories = true;
    categoriesAreDates = true;
  }

  if (!firstColumnAsCategories) {
    for (let i = categoryOffset; i < categories.length; i++) {
      if (typeof categories[i] === 'string') {
        firstColumnAsCategories = true;
        break;
      }
    }
  }
  if (!options.isScatterplot)
    series = writeSeriesNames(series, firstRowAsNames, firstColumnAsCategories);
  series = writeSeriesMarkdown(series);
  series = writeSeriesLines(series);
  series = writeShowInLegend(series);

  // console.log('********** DATA **********');
  // console.log(series);
  // console.log(parsed);
  // console.log('categories:', categories);
  // console.log('suppress:', suppress);
  // console.log('firstRowAsNames:', firstRowAsNames);
  // console.log('firstColumnAsCategories:', firstColumnAsCategories);
  // console.log('categoriesAreDates:', categoriesAreDates);
  // console.log('********** //// **********');

  const [dateSpan, interval] = categoriesAreDates ? findDateSpan(dates) : [null, null];

  return {
    type,
    parsed,
    series,
    categories,
    firstRowAsNames,
    firstColumnAsCategories,
    categoriesAreDates,
    categoriesAreMonthly,
    fiscalYears,
    quarterlyResults,
    dateSpan,
    interval,
  };
}

function isQuarterly(dates) {
  if (dates.every((date) => date.quarter)) return true;
  if (dates.length === 1) return false;
  for (let i = 0; i < dates.length - 1; i++) {
    if (dates[i + 1]) {
      const [currentMonth, nextMonth] = [i, i + 1].map((idx) => dates[idx].month);
      if (nextMonth + 1 - ((currentMonth + 1) % 12) !== 3) return false;
    }
  }
  return true;
}

function createDates(categories, options = {}) {
  return chartEngine.datetime.parse(categories, options);
}

function findDateSpan(dates) {
  const values = dates.map(({ value }) => value);
  let lastDelta;
  for (let i = 0; i < values.length; i++) {
    const value = values[i];
    const nextValue = values[i + 1];
    const delta = nextValue - value;
    if (delta === lastDelta) {
      break;
    }
    lastDelta = delta;
  }
  const minDate = Math.min(...values);
  const maxDate = Math.max(...values);
  return [maxDate - minDate, Math.abs(lastDelta)];
}

// Sort the raw parsed data into series.
function sortParsed(parsed, options) {
  const series = [];
  const seriesNames = [];
  const seriesNamesObj = [];
  const numberOfCols = parsed[0].length;
  parsed.forEach((row, r) => {
    if (options.isScatterplot) {
      const nameIndex = numberOfCols === 4 ? 3 : 0;
      series[r] = series[r] || { data: [] };
      series[r].name = row[nameIndex];
      if (!seriesNames.includes(row[nameIndex])) {
        seriesNames.push(row[nameIndex]);
        seriesNamesObj.push({
          name: row[nameIndex],
          data: [{ y: row[2], x: row[1], value: row[0] }],
        });
      } else {
        const index = seriesNames.indexOf(row[nameIndex]);
        seriesNamesObj[index].data.push({
          y: row[2],
          x: row[1],
          value: row[0],
        });
      }
    } else {
      row.forEach((item, c) => {
        series[c] = series[c] || { data: [] };
        if (/^\s*$/.test(item)) {
          series[c].data[r] = { y: null };
        } else {
          series[c].data[r] = { y: toNum(item) };
        }
      });
    }
  });
  return options.isScatterplot ? seriesNamesObj : series;
}

// Tests if data has no data; i.e., is junk.
function isJunk(series) {
  if (
    series.length === 1 &&
    (series[0].data[0] === undefined || typeof series[0].data[0].y === 'string')
  ) {
    return true;
  }
  return false;
}

// Tests if data is missing items.
function hasUndefinedItems(series) {
  loop: for (let s = 0; s < series.length; s++) {
    for (let d = 0; d < series[s].data.length; d++) {
      if (series[s].data[d] === undefined) {
        break loop;
      }
    }
  }
  return hasUndefined;
}

// Remove empty data lines (which correspond to just a newline in the raw data).
function removeEmptyLines(parsed) {
  const parsedLen = parsed.length;
  let index = parsedLen - 1;
  while (parsed[index].length === 1 && /^\s*$/.test(parsed[index][0])) {
    parsed.pop();
    --index;
  }
  return parsed;
}

// Determines if a column of data is composed entirely of years or not.
// Not known
function isSeriesYears(column, firstRowAsNames) {
  const colLen = column.length;
  const { categoryOffset } = getOffsets({ firstRowAsNames });
  if (colLen - categoryOffset === 0) {
    return false;
  }
  for (let i = categoryOffset; i < colLen; i++) {
    const value = column[i].y;
    if (typeof value === 'number') {
      if (!isNaN(value) && value >= 1000 && value < 3000) {
        continue;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }
  return true;
}

// Determines if firstRowAsNames is true for @param series. If any strings are
// found in the first row of any series, then the `firstRowAsNames` should be
// considered true. `firstColumnAsCategories` is not known yet so special
// consideration is given to the first row of the first series.
function getFirstRowAsNames(series, options) {
  const seriesLen = series.length;
  for (let i = 0; i < seriesLen; i++) {
    const { y } = series[i].data[0];
    if (i === 0 && seriesLen > 1 && y === null) {
      // If the first index of the first series is null:
      //   S S S
      // C n n n
      // C n n n
      return true;
    }
    if (i > 0 && isStr(y) && !chartEngine.datetime.isQuarterlyString(y)) {
      return true;
    }
  }
  if (isValidDate(series)) return true;
  if (options.firstRowAsNames) return true;
  return false;
}

function isStr(value) {
  if (typeof value === 'string') return true;
}
function isValidDate(series) {
  const valueArr = [];
  for (let r = 1; r < series.length; r++) {
    const value = series[r].data[0].y;
    if (typeof value === 'number') {
      if (value % 1 !== 0) return false;
      if (value.toString().length !== 4) return false;
      valueArr.push(value);
    }
  }
  return getViableYears(valueArr);
}

function getViableYears(areTheyYears) {
  const arr = [];
  for (let u = 1800; u < 2100; u++) {
    arr.push(u);
  }
  for (let t = 0; t < areTheyYears.length; t++) {
    if (arr.indexOf(areTheyYears[t]) === -1) {
      return false;
    }
  }
  return true;
}

function writeSeriesNames(series, firstRowAsNames, firstColumnAsCategories) {
  const { seriesOffset } = getOffsets({ firstColumnAsCategories });
  series.forEach((seriesData, i) => {
    if (firstRowAsNames) {
      seriesData.name = seriesData.data[0].y;
    } else {
      const n = i + seriesOffset;
      seriesData.name = `Series ${n}`;
    }
  });
  return series;
}

function writeSeriesMarkdown(series) {
  series.forEach((seriesData) => {
    if (!seriesData.markdown) seriesData.markdown = '{series}';
  });
  return series;
}

function writeShowInLegend(series) {
  series.forEach((seriesData) => {
    if (seriesData.showInLegend === undefined) seriesData.showInLegend = true;
  });
  return series;
}

function writeSeriesLines(series) {
  series.forEach((seriesData) => {
    if (!seriesData.line) seriesData.line = 'solid';
  });
  return series;
}

// Convert @param value to number, if possible.
function toNum(value) {
  let n = +value;
  if (isNaN(n) && typeof value === 'string') {
    n = +value.replace(/,|\s/g, '');
  }
  return isNaN(n) ? value : n;
}

function getDataType(type, econType) {
  switch (type) {
    case 'market':
      return 'Market Data';
    case 'econ':
      return `${econType} Data`;
    default:
      return 'Copy-Pasted Data';
  }
}
